import { twMerge } from 'tailwind-merge';

// Device app-version indicator (#1530): shows which build each of a user's
// live devices is running, so support can tell who is on an old/unupdated app
// (this bit us when nobody could tell which iOS build a user's phone was on).
// One helper + one badge so every surface renders it identically — the admin
// user detail page and the support-chat DM header (alongside the VIP badge).

export type DeviceVersion = {
  platform?: 'ios' | 'android' | 'web' | string | null;
  lastSeenAt?: string | null;
  appVersion?: string | null;
  nativeBuild?: string | null;
  nativeVersion?: string | null;
  otaBundle?: string | null;
};

// The admin's own loaded web bundle date (unix seconds), i.e. the current
// deployed JS. A device whose reported web bundle is older than this is behind.
const CURRENT_WEB_BUNDLE = parseInt(`${import.meta.env.VITE_PUBLIC_ENV__APP_DATE ?? ''}`, 10) || 0;

// The web bundle date is a unix-seconds timestamp (VITE_PUBLIC_ENV__APP_DATE /
// the x-ras-cd header). Render it as YYYY.MM.DD; return null if it isn't one.
function formatBundleDate(appVersion?: string | null): string | null {
  const secs = parseInt(`${appVersion ?? ''}`, 10);
  if (!secs) {
    return null;
  }
  const d = new Date(secs * 1000);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${y}.${m}.${day}`;
}

function hasAnyVersion(d: DeviceVersion): boolean {
  return !!(d.appVersion || d.nativeBuild || d.nativeVersion || d.otaBundle);
}

// True when the device's web bundle is older than the admin's current one.
export function isDeviceBundleStale(d: DeviceVersion): boolean {
  const deviceBundle = parseInt(`${d.appVersion ?? ''}`, 10) || 0;
  return CURRENT_WEB_BUNDLE > 0 && deviceBundle > 0 && deviceBundle < CURRENT_WEB_BUNDLE;
}

// Human label for one device, e.g. "iOS v1.1 (build 4011) · web 2026.09.22" or
// "Android v1.1 (build 4508) · OTA 2026.09.16-4465". Four distinct ideas, each
// labeled so they can't be mistaken for one another (#2089): the store
// version name (nativeVersion), the strictly-increasing build number
// (nativeBuild), the deployed web/JS bundle date (appVersion), and the
// currently-applied OTA bundle (otaBundle).
export function formatDeviceVersion(d: DeviceVersion): string {
  const parts: string[] = [];
  const platform = `${d.platform ?? ''}`.toLowerCase();
  const platformLabel =
    platform === 'ios' ? 'iOS' : platform === 'android' ? 'Android' : platform || null;

  if (d.nativeBuild || d.nativeVersion) {
    const bits = [platformLabel || 'native'];
    if (d.nativeVersion) {
      bits.push(`v${d.nativeVersion}`);
    }
    if (d.nativeBuild) {
      bits.push(`(build ${d.nativeBuild})`);
    }
    parts.push(bits.join(' '));
  } else if (platform === 'web') {
    parts.push('web');
  } else if (platformLabel) {
    parts.push(platformLabel);
  }

  const bundleDate = formatBundleDate(d.appVersion);
  if (bundleDate) {
    // Avoid a redundant bare "web" chip when we also show the web bundle date.
    if (parts[parts.length - 1] === 'web') {
      parts[parts.length - 1] = `web ${bundleDate}`;
    } else {
      parts.push(`web ${bundleDate}`);
    }
  }

  if (d.otaBundle) {
    parts.push(`OTA ${d.otaBundle}`);
  }

  return parts.join(' · ') || 'unknown build';
}

/**
 * Renders one chip per live device carrying version info. Stale devices (web
 * bundle behind the current deploy) get an amber chip; current ones are neutral.
 * Renders nothing when no device reports a version (e.g. web-only users or
 * pre-#1530 clients that haven't checked in yet).
 */
export function DeviceVersionBadge({
  devices,
  className,
}: {
  devices?: DeviceVersion[] | null;
  className?: string;
}) {
  const withVersion = (devices ?? []).filter(hasAnyVersion);
  if (!withVersion.length) {
    return null;
  }

  return (
    <span className={twMerge('inline-flex flex-wrap items-center gap-1', className)}>
      {withVersion.map((d, i) => {
        const stale = isDeviceBundleStale(d);
        const seen = d.lastSeenAt ? new Date(d.lastSeenAt).toLocaleDateString() : null;
        return (
          <span
            // Devices have no stable id here; index is fine for a static list.
            key={`${d.platform}-${d.nativeBuild}-${d.appVersion}-${i}`}
            className={twMerge(
              'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 font-medium text-xs',
              stale
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-400/15 dark:text-amber-300'
                : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300',
            )}
            title={`${formatDeviceVersion(d)}${stale ? ' — behind current web build' : ''}${
              seen ? ` (last seen ${seen})` : ''
            }`}
          >
            {formatDeviceVersion(d)}
            {stale ? ' ⚠' : ''}
          </span>
        );
      })}
    </span>
  );
}
